import React, { useMemo, useState } from 'react';
import { Col, Row, formatAmountText } from '../../lib/utils';
import { useAmountInputCaret } from '../../lib/amountInputCaret';
import { colors } from '../../lib/colors';
import { Body2Regular, Subtitle1Bold } from '../../lib/font';
import ColorSquareButton from '../Button/ColorSquareButton';
import WhiteSquareButton from '../Button/WhiteSquareButton';
import dayjs from 'dayjs';
import { useFacilityProductList } from '@/features/facilityProduct/hooks';
import { useCreateSubscription } from '@/features/facilitySubscription/hooks';
import { IFacilityProduct } from '@/features/facilityProduct/types';
import { PaymentMethod } from '@/features/facilitySubscription/types';
import { handleLockerConflict } from '@/features/facilitySubscription/lockerConflict';
import { FacilitySubscriptionInfoType } from '../../api/member';

const PAYMENT_OPTIONS: { code: PaymentMethod; label: string }[] = [
    { code: 'card', label: '카드' },
    { code: 'cash', label: '현금' },
    { code: 'transfer', label: '이체' },
];

type CategoryKey = 'all' | 'gym' | 'locker' | 'clothing';
const CATEGORY_TABS: { key: CategoryKey; label: string; color: string; bg: string }[] = [
    { key: 'all', label: '전체', color: '#1E40AF', bg: '#BFDBFE' },
    { key: 'gym', label: '헬스', color: '#065F46', bg: '#A7F3D0' },
    { key: 'locker', label: '락커', color: '#92400E', bg: '#FDE68A' },
    { key: 'clothing', label: '운동복', color: '#5B21B6', bg: '#DDD6FE' },
];
const CATEGORY_LABEL: Record<string, string> = { gym: '헬스', locker: '락커', clothing: '운동복', etc: '기타' };

const getMutationErrorMessage = (error: any, fallback: string) => error?.serverMessage || error?.message || fallback;

interface Props {
    isVisible: boolean;
    onClose: () => void;
    centerId: string;
    memberId?: string;
    createAlert: any;
    // 수집 모드: API 호출 없이 입력값을 콜백으로 반환 (임시회원 등록 플로우 — 회원 등록 완료 시 함께 등록)
    onCollect?: (data: FacilitySubscriptionInfoType) => void;
}

const SubscriptionRegisterModal = ({ isVisible, onClose, centerId, memberId, createAlert, onCollect }: Props) => {
    const { data: productData, isLoading: productsLoading, isError: productsError } = useFacilityProductList({ centerId, enabled: isVisible });
    const createMutation = useCreateSubscription();

    const products = (productData?.results ?? []).filter((p: IFacilityProduct) => p.isActive && p.category !== 'etc');

    const [selectedProductId, setSelectedProductId] = useState('');
    const [startDate, setStartDate] = useState(dayjs().format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState('');
    // 콤마 없는 숫자 문자열만 담는다. 제출은 그대로 Number(price) 를 쓴다.
    const [price, setPrice] = useState('');
    const amountCaret = useAmountInputCaret({ allowDecimal: true });
    const [paymentDate, setPaymentDate] = useState(dayjs().format('YYYY-MM-DD'));
    const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('card');
    const [memo, setMemo] = useState('');
    const [lockerNumber, setLockerNumber] = useState('');
    const [lockNumber, setLockNumber] = useState('');
    const [categoryFilter, setCategoryFilter] = useState<CategoryKey>('all');
    const [searchQuery, setSearchQuery] = useState('');

    const selectedProduct = products.find((p: IFacilityProduct) => p._id === selectedProductId);

    // 카테고리별 개수
    const categoryCounts = useMemo(() => {
        const base: Record<string, number> = { all: products.length, gym: 0, locker: 0, clothing: 0 };
        products.forEach((p: IFacilityProduct) => {
            base[p.category] = (base[p.category] || 0) + 1;
        });
        return base;
    }, [products]);

    // 필터 + 검색 적용된 표시 상품
    const visibleProducts = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        return products.filter((p: IFacilityProduct) => {
            if (categoryFilter !== 'all' && p.category !== categoryFilter) return false;
            if (q && !p.name.toLowerCase().includes(q)) return false;
            return true;
        });
    }, [products, categoryFilter, searchQuery]);

    const handleProductSelect = (product: IFacilityProduct) => {
        setSelectedProductId(product._id);
        // 상품이 값을 갈아끼우는 자리 — 낡은 커서 복원을 버린다.
        amountCaret.dropPendingCaret();
        setPrice(String(product.price));
        setEndDate(dayjs(startDate).add(product.duration, 'day').format('YYYY-MM-DD'));
    };

    const handleStartDateChange = (newStartDate: string) => {
        setStartDate(newStartDate);
        if (selectedProduct) {
            setEndDate(dayjs(newStartDate).add(selectedProduct.duration, 'day').format('YYYY-MM-DD'));
        }
    };

    const lockerRequired = selectedProduct?.category === 'locker' && !lockerNumber.trim();
    const isValid = selectedProductId && startDate && price && paymentDate && !lockerRequired;

    const handleSubmit = async (force = false) => {
        if (!isValid) return;
        // 수집 모드: 서버 등록 없이 입력값만 반환
        if (onCollect) {
            onCollect({
                facilityProductId: selectedProductId,
                productName: selectedProduct?.name ?? '',
                category: selectedProduct?.category ?? '',
                startDate,
                endDate,
                price: Number(price),
                paymentMethod,
                paymentDate,
                memo: memo.trim() || undefined,
                lockerNumber: selectedProduct?.category === 'locker' && lockerNumber.trim() ? lockerNumber.trim() : undefined,
            });
            onClose();
            resetForm();
            return;
        }
        if (!memberId) return;
        try {
            await createMutation.mutateAsync({
                centerId,
                memberId,
                body: {
                    facilityProductId: selectedProductId,
                    startDate,
                    endDate,
                    price: Number(price),
                    paymentDate,
                    paymentMethod,
                    memo: memo.trim() || undefined,
                    lockerNumber: selectedProduct?.category === 'locker' && lockerNumber.trim() ? lockerNumber.trim() : undefined,
                    lockNumber: selectedProduct?.category === 'locker' && lockNumber.trim() ? lockNumber.trim() : undefined,
                    ...(force ? { force: true } : {}),
                },
            });
            onClose();
            resetForm();
            createAlert('등록 완료', '이용권이 등록되었습니다.', false);
        } catch (error) {
            // 라커 중복 경고면 확인 후 강행 등록
            if (handleLockerConflict(error, createAlert, () => handleSubmit(true))) return;
            createAlert('등록 실패', getMutationErrorMessage(error, '이용권 등록에 실패했습니다.'), true);
        }
    };

    const resetForm = () => {
        setSelectedProductId('');
        setStartDate(dayjs().format('YYYY-MM-DD'));
        setEndDate('');
        setPrice('');
        setPaymentDate(dayjs().format('YYYY-MM-DD'));
        setPaymentMethod('card');
        setMemo('');
        setLockerNumber('');
        setLockNumber('');
        setCategoryFilter('all');
        setSearchQuery('');
    };

    if (!isVisible) return null;

    const inputClass = 'border border-[#E1E4E6] rounded-[8px] px-[12px] py-[10px] text-[14px] w-full';

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
            <Col className="bg-white rounded-[16px] p-[32px] w-[480px] max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <Subtitle1Bold>이용권 등록</Subtitle1Bold>

                <Col className="mt-[24px] gap-[16px]">
                    {/* 상품 선택 */}
                    <Col className="gap-[8px]">
                        <Body2Regular color={colors.LAVEL_2}>이용권 선택</Body2Regular>
                        {productsLoading ? (
                            <Row className="items-center gap-[8px]" style={{ border: '1px solid #E1E4E6', borderRadius: 8, padding: '10px 12px' }}>
                                <div
                                    style={{
                                        width: 14,
                                        height: 14,
                                        border: '2px solid #E5E7EB',
                                        borderTopColor: '#3B82F6',
                                        borderRadius: '50%',
                                        animation: 'spin 0.8s linear infinite',
                                    }}
                                />
                                <span style={{ fontSize: 13, color: '#9CA3AF' }}>이용권 목록 불러오는 중...</span>
                            </Row>
                        ) : productsError ? (
                            <Row
                                className="items-center gap-[8px]"
                                style={{ border: '1px solid #FCA5A5', borderRadius: 8, padding: '10px 12px', backgroundColor: '#FEF2F2' }}
                            >
                                <span style={{ fontSize: 13, color: '#EF4444' }}>이용권 목록을 불러오지 못했습니다. 잠시 후 다시 시도해주세요.</span>
                            </Row>
                        ) : products.length === 0 ? (
                            <Col style={{ padding: '16px', borderRadius: 8, backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', alignItems: 'center' }}>
                                <span style={{ fontSize: 13, color: '#9CA3AF' }}>등록된 이용권 상품이 없습니다</span>
                            </Col>
                        ) : (
                            <>
                                {/* 검색 */}
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="이용권 이름으로 검색"
                                    style={{
                                        border: '1px solid #E1E4E6',
                                        borderRadius: 8,
                                        padding: '8px 12px',
                                        fontSize: 13,
                                        outline: 'none',
                                    }}
                                />

                                {/* 카테고리 탭 */}
                                <Row style={{ gap: 6, flexWrap: 'wrap' }}>
                                    {CATEGORY_TABS.map((tab) => {
                                        const count = categoryCounts[tab.key] ?? 0;
                                        const active = categoryFilter === tab.key;
                                        // 상품이 0개인 카테고리(전체 제외)는 비활성 스타일
                                        const hasItems = tab.key === 'all' || count > 0;
                                        return (
                                            <button
                                                key={tab.key}
                                                type="button"
                                                onClick={() => hasItems && setCategoryFilter(tab.key)}
                                                disabled={!hasItems}
                                                style={{
                                                    padding: '5px 12px',
                                                    borderRadius: 16,
                                                    fontSize: 12,
                                                    fontWeight: active ? 700 : 500,
                                                    cursor: hasItems ? 'pointer' : 'not-allowed',
                                                    border: active ? `1.5px solid ${tab.color}` : '1px solid #E5E7EB',
                                                    backgroundColor: active ? tab.bg : 'white',
                                                    color: active ? tab.color : hasItems ? '#6B7280' : '#D1D5DB',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 4,
                                                    transition: 'all 0.15s',
                                                }}
                                            >
                                                {tab.label}
                                                <span
                                                    style={{
                                                        fontSize: 10,
                                                        padding: '0 5px',
                                                        borderRadius: 8,
                                                        backgroundColor: active ? 'rgba(255,255,255,0.55)' : '#F3F4F6',
                                                        color: active ? tab.color : '#9CA3AF',
                                                        fontWeight: 700,
                                                    }}
                                                >
                                                    {count}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </Row>

                                {/* 결과 리스트 */}
                                <Col style={{ gap: 4, maxHeight: 240, overflowY: 'auto' }}>
                                    {visibleProducts.length === 0 ? (
                                        <Col style={{ padding: '24px 12px', alignItems: 'center', gap: 4 }}>
                                            <span style={{ fontSize: 13, color: '#9CA3AF' }}>
                                                {searchQuery ? '검색 결과가 없습니다' : '이 카테고리에 등록된 이용권이 없습니다'}
                                            </span>
                                        </Col>
                                    ) : (
                                        visibleProducts.map((p: IFacilityProduct) => {
                                            const sel = selectedProductId === p._id;
                                            const catLabel = CATEGORY_LABEL[p.category] || p.category;
                                            return (
                                                <button
                                                    key={p._id}
                                                    onClick={() => handleProductSelect(p)}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 10,
                                                        padding: '10px 12px',
                                                        borderRadius: 8,
                                                        cursor: 'pointer',
                                                        textAlign: 'left',
                                                        border: sel ? '1.5px solid #3B82F6' : '1px solid #E5E7EB',
                                                        backgroundColor: sel ? '#EFF6FF' : 'white',
                                                        transition: 'all 0.15s',
                                                    }}
                                                >
                                                    {/* 라디오 표시 */}
                                                    <div
                                                        style={{
                                                            width: 16,
                                                            height: 16,
                                                            borderRadius: '50%',
                                                            border: sel ? '5px solid #3B82F6' : '1.5px solid #D1D5DB',
                                                            backgroundColor: 'white',
                                                            flexShrink: 0,
                                                        }}
                                                    />
                                                    {/* 이름 */}
                                                    <span
                                                        style={{
                                                            flex: 1,
                                                            fontSize: 13,
                                                            fontWeight: sel ? 600 : 500,
                                                            color: sel ? '#1E40AF' : '#111827',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        {p.name}
                                                    </span>
                                                    {/* 카테고리 + 기간 */}
                                                    <span
                                                        style={{
                                                            fontSize: 11,
                                                            color: '#6B7280',
                                                            padding: '1px 6px',
                                                            borderRadius: 4,
                                                            backgroundColor: '#F3F4F6',
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        {catLabel} · {p.duration}일
                                                    </span>
                                                    {/* 가격 */}
                                                    <span
                                                        style={{
                                                            fontSize: 13,
                                                            fontWeight: 700,
                                                            color: sel ? '#1E40AF' : '#374151',
                                                            flexShrink: 0,
                                                            minWidth: 80,
                                                            textAlign: 'right',
                                                        }}
                                                    >
                                                        {p.price.toLocaleString()}원
                                                    </span>
                                                </button>
                                            );
                                        })
                                    )}
                                </Col>
                            </>
                        )}
                    </Col>

                    {/* 시작일 */}
                    <Col className="gap-[6px]">
                        <Body2Regular color={colors.LAVEL_2}>시작일</Body2Regular>
                        <input className={inputClass} type="date" value={startDate} onChange={(e) => handleStartDateChange(e.target.value)} />
                    </Col>

                    {/* 종료일 (자동 계산) */}
                    {endDate && (
                        <Col className="gap-[6px]">
                            <Body2Regular color={colors.LAVEL_2}>종료일 (자동 계산)</Body2Regular>
                            <input className={inputClass} type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                        </Col>
                    )}

                    {/* 금액 */}
                    <Col className="gap-[6px]">
                        <Body2Regular color={colors.LAVEL_2}>결제 금액 (원)</Body2Regular>
                        <input
                            className={inputClass}
                            type="text"
                            inputMode="numeric"
                            value={formatAmountText(price)}
                            onChange={(e) => {
                                // 원칙 1 — 패턴에 안 맞으면 값을 아예 바꾸지 않는다.
                                const digits = amountCaret.changeAmount(e, price);
                                if (digits == null) return;
                                setPrice(digits);
                            }}
                            // 붙여넣기는 한 번에 값이 확정되므로 정규형만 받는다(CSV 조각 차단).
                            onPaste={amountCaret.pasteAmount}
                            onDrop={amountCaret.dropAmount}
                        />
                    </Col>

                    {/* 결제 수단 */}
                    <Col className="gap-[6px]">
                        <Body2Regular color={colors.LAVEL_2}>결제 수단</Body2Regular>
                        <Row className="gap-[8px]">
                            {PAYMENT_OPTIONS.map((opt) => {
                                const sel = paymentMethod === opt.code;
                                return (
                                    <button
                                        key={opt.code}
                                        style={{
                                            flex: 1,
                                            padding: '8px 0',
                                            borderRadius: 8,
                                            fontSize: 13,
                                            fontWeight: sel ? 600 : 400,
                                            backgroundColor: sel ? '#3B82F6' : '#fff',
                                            color: sel ? '#fff' : '#4B5563',
                                            border: sel ? '1px solid #3B82F6' : '1px solid #E1E4E6',
                                            cursor: 'pointer',
                                        }}
                                        onClick={() => setPaymentMethod(opt.code)}
                                    >
                                        {opt.label}
                                    </button>
                                );
                            })}
                        </Row>
                    </Col>

                    {/* 결제일 */}
                    <Col className="gap-[6px]">
                        <Body2Regular color={colors.LAVEL_2}>결제일</Body2Regular>
                        <input className={inputClass} type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} />
                    </Col>

                    {/* 메모 */}
                    <Col className="gap-[6px]">
                        <Body2Regular color={colors.LAVEL_2}>메모 (선택)</Body2Regular>
                        <textarea className={`${inputClass} resize-none`} rows={2} value={memo} onChange={(e) => setMemo(e.target.value)} />
                    </Col>

                    {/* 라커 배정 필드 (라커 상품일 때만) */}
                    {selectedProduct?.category === 'locker' && (
                        <Col className="gap-[12px] p-[16px] bg-[#F8FAFC] rounded-[10px] border border-solid border-[#E2E8F0]">
                            <Row className="items-center gap-[6px] mb-[4px]">
                                <Subtitle1Bold color={colors.LAVEL_1} style={{ fontSize: 14 }}>
                                    라커 배정
                                </Subtitle1Bold>
                            </Row>
                            <Col className="gap-[6px]">
                                <Row className="items-center gap-[4px]">
                                    <label className="text-[12px] font-bold text-gray-700">번호</label>
                                    <span className="text-[11px] text-red-500">*</span>
                                </Row>
                                <input
                                    className={inputClass}
                                    placeholder="예: 12"
                                    value={lockerNumber}
                                    onChange={(e) => setLockerNumber(e.target.value)}
                                    style={{ borderColor: lockerRequired ? '#EF4444' : undefined }}
                                />
                            </Col>
                            {/* 수집 모드에서는 자물쇠 비밀번호 미지원 (임시회원 등록 API에 필드 없음) */}
                            {!onCollect && (
                                <Col className="gap-[6px]">
                                    <label className="text-[12px] font-bold text-gray-700">자물쇠 비밀번호 (선택)</label>
                                    <input className={inputClass} placeholder="예: 1234" value={lockNumber} onChange={(e) => setLockNumber(e.target.value)} />
                                    {/*
                                      * 이 입력은 **회원 개별값**을 저장한다. 자리에 붙은 물리 자물쇠 번호를 여기에
                                      * 적으면 그 회원만 칸의 고정값을 상속하지 않게 된다(TICKET_LOCKER_INDIVIDUAL_LOCK_BREAKS_INHERITANCE.md).
                                      */}
                                    <span className="text-[11px] text-gray-500">이 값은 이 회원에게만 적용됩니다. 자리에 고정된 비밀번호는 「락커 관리」에서 설정하세요.</span>
                                </Col>
                            )}
                            {lockerRequired && <span className="text-[11px] text-red-500 mt-[4px]">라커 번호는 필수입니다.</span>}
                        </Col>
                    )}
                </Col>

                <Row className="mt-[24px] gap-[12px] justify-end">
                    <WhiteSquareButton text="취소" size="small" callBack={onClose} />
                    <ColorSquareButton text="등록" size="small" isDisabled={!isValid || createMutation.isLoading} callBack={() => handleSubmit()} />
                </Row>
            </Col>
        </div>
    );
};

export default SubscriptionRegisterModal;
